import React, { useEffect, useState } from "react";
import { NavigationContainer } from "@react-navigation/native";
import { createMaterialBottomTabNavigator } from "@react-navigation/material-bottom-tabs";
import DiagnosisNavigator from "./diagnose/DiagnosisNavigator";
import DiaryNavigator from "./diary/DiaryNavigator";
import ChatNavigator from "./chat/ChatNavigator";
import GroupNavigator from "./group/GroupNavigator";
import SettingNavigator from "./setting/SettingNavigator";
import GroupList from "./group/GroupList";
import * as AppConst from "./components/AppConst";
import {
  AppUser,
  GroupConversation,
  Message,
  SendPicture,
  UserType,
  Cooperation,
} from "../models";
import { DataStore, Auth } from "aws-amplify";
import {
  getStorage,
  saveStorage,
  removeStorage,
  allRemoveStorage,
} from "./components/SessionStorage";
import { handleCognitoErrorMessage } from "./components/CmnUtil";
import { ConfirmDialog } from "./components/CmnDiaLog";
import { View, Image } from "react-native";
import IconImages from "./components/IconImages";
import * as CmnUtil from "./components/CmnUtil";

// タブを表示するための記述
const Tab = createMaterialBottomTabNavigator();

/** ================================
 *  タブナビゲーション
 *  ================================*/
function TabNavigator() {
  const [authUser, setAuthUser] = useState<AppUser | undefined>();
  const [groupProducer, setGroupProducer] = useState<string | undefined>();

  // エラーダイアログ
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorModal, setIsErrorModal] = useState(false);

  // 未読数
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    // ログインユーザー情報保持
    AppAuthLogin();
    // 未読数の集計
    countUnread();

    // チャット情報の購読
    const groupConversation =
      DataStore.observe(GroupConversation).subscribe(countUnread);
    const message = DataStore.observe(Message).subscribe(countUnread);
    const sendPicture = DataStore.observe(SendPicture).subscribe(countUnread);
    return () => {
      groupConversation.unsubscribe();
      message.unsubscribe();
      sendPicture.unsubscribe();
    };
  }, []);

  /**
   * アプリユーザログイン
   */
  const AppAuthLogin = async () => {
    await Auth.currentAuthenticatedUser()
      .then((currentUser) => {
        GetAppUser(currentUser);
      })
      .catch((error) => {
        handleError(error);
      });
  };

  /**
   * AppUser取得
   */
  const GetAppUser = async (currentUser: any) => {
    await DataStore.query(AppUser, (c) =>
      c.authUsername("eq", currentUser.attributes.email)
    )
      .then(async (app_user_data) => {
        if (app_user_data.length !== 0) {
          // システム管理者と無効ユーザはログイン不可
          if (
            (app_user_data[0].group.groupType !== UserType.PRODUCER &&
              app_user_data[0].group.groupType !== UserType.INSTRUCTOR) ||
            app_user_data[0].isEnabled !== true
          ) {
            setErrorMessage("操作権限がありません。");
            setIsErrorModal(true);
          } else {
            if (app_user_data[0].group.groupType === UserType.INSTRUCTOR) {
              let tmpProducer = await getStorage("friendProducer");
              if (tmpProducer != undefined) {
                const cooperationList = await DataStore.query(Cooperation, (c) =>
                  c
                    .groupID("eq", app_user_data[0].group.id)
                    .cooperationGroupID("eq", tmpProducer)
                );
                if (cooperationList.length == 0) {
                  tmpProducer = undefined;
                  await removeStorage("friendProducer");
                }
              }
              setGroupProducer(tmpProducer);
            }
            setAuthUser(app_user_data[0]);
            saveAppUser(app_user_data[0]);
          }
        } else {
          setErrorMessage("ユーザが見つかりません。");
          setIsErrorModal(true);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  };

  /**
   * セッションストレージ保存
   */
  const saveAppUser = async (app_user_data: any) => {
    await saveStorage("authUser", {
      userId: app_user_data.id,
      groupId: app_user_data.group.id,
      userType: app_user_data.group.groupType,
    });
  };

  /**
   * ログアウト処理
   */
  const signOut = async () => {
    await Auth.signOut()
      .then(() => {
        setIsErrorModal(!isErrorModal);
        allRemoveStorage();
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => {
        document.location.reload();
      });
  };

  /**
   * エラーダイアログ表示
   */
  function handleError(error: any) {
    const errorInfo = handleCognitoErrorMessage(error);
    setErrorMessage(errorInfo.message);
    setIsErrorModal(true);
  }

  // 未読数の集計
  const countUnread = async () => {
    let tmpUnread = 0;

    const appUser = await CmnUtil.getCurrentUser();
    const groupConversations = (await DataStore.query(GroupConversation)).filter(
      (c) => c.group.id === appUser?.group.id
    );
    for (let i = 0; i < groupConversations.length; i++) {
      const groupConversation = groupConversations[i];
      // 未読メッセージ数取得
      const message = (await DataStore.query(Message)).filter(
        (c) =>
          c.conversation.id === groupConversation.conversation.id &&
          c.sender.id != appUser?.id &&
          c.isRead == false
      );
      const sendPicture = (await DataStore.query(SendPicture)).filter(
        (c) =>
          c.conversation.id === groupConversation.conversation.id &&
          c.sender.id != appUser?.id &&
          c.isRead == false
      );
      tmpUnread = tmpUnread + message.length + sendPicture.length;
    }
    setUnread(tmpUnread);
  };

  return authUser ? (
    authUser.type === UserType.INSTRUCTOR && groupProducer === undefined ? (
      <GroupList
        navigation={undefined}
        setGroupProducer={setGroupProducer}
      />
    ) : (
      <NavigationContainer>
        <Tab.Navigator
          shifting={false}
          initialRouteName="Feed"
          activeColor="#ffffff"
          barStyle={{ backgroundColor: AppConst.buttonColor }}
        >
          {/* 作業日誌 */}
          <Tab.Screen
            name="DiaryNavigator"
            component={DiaryNavigator}
            options={{
              tabBarLabel: "作業日誌",
              tabBarIcon: ({ focused }) => (
                <Image
                  source={{ uri: IconImages.icBook }}
                  style={{
                    width: 26,
                    height: 26,
                    marginHorizontal: 15,
                    tintColor: focused ? "#FFFFFF" : "#333333",
                  }}
                />
              ),
            }}
          />

          {/* AI診断 */}
          <Tab.Screen
            name="DiagnosisNavigator"
            component={DiagnosisNavigator}
            options={{
              tabBarLabel: "AI診断",
              tabBarIcon: ({ focused }) => (
                <Image
                  source={{ uri: IconImages.icRobot }}
                  style={{
                    width: 26,
                    height: 26,
                    marginHorizontal: 15,
                    tintColor: focused ? "#FFFFFF" : "#333333",
                  }}
                />
              ),
            }}
          />

          {/* チャット */}
          <Tab.Screen
            name="ChatNavigator"
            component={ChatNavigator}
            options={{
              tabBarLabel: "チャット",
              tabBarIcon: ({ focused }) => (
                <>
                  <Image
                    source={{ uri: IconImages.icChat }}
                    style={{
                      width: 26,
                      height: 26,
                      marginHorizontal: 15,
                      tintColor: focused ? "#FFFFFF" : "#333333",
                    }}
                  />
                  <View style={{ position: "absolute", right: -10, top: -5 }}>
                    <CmnUtil.Badge count={unread} />
                  </View>
                </>
              ),
            }}
          />

          {/* 協力組織 */}
          <Tab.Screen
            name="GroupNavigator"
            component={GroupNavigator}
            options={{
              tabBarLabel: "協力組織",
              tabBarIcon: ({ focused }) => (
                <Image
                  source={{ uri: IconImages.icFriend }}
                  style={{
                    width: 26,
                    height: 26,
                    marginHorizontal: 15,
                    tintColor: focused ? "#FFFFFF" : "#333333",
                  }}
                />
              ),
            }}
          />

          {/* 設定 */}
          <Tab.Screen
            name="SettingNavigator"
            component={SettingNavigator}
            options={{
              tabBarLabel: "設定",
              tabBarIcon: ({ focused }) => (
                <Image
                  source={{ uri: IconImages.icSetting }}
                  style={{
                    width: 26,
                    height: 26,
                    marginHorizontal: 15,
                    tintColor: focused ? "#FFFFFF" : "#333333",
                  }}
                />
              ),
            }}
          />
        </Tab.Navigator>
      </NavigationContainer>
    )
  ) : (
    <>
      <ConfirmDialog
        confirmButtonName="OK"
        message={errorMessage}
        visible={isErrorModal}
        confirmAction={() => {
          signOut();
        }}
      />
    </>
  );
}

export default TabNavigator;
